import React from "react";
import { Button, Space, Typography, Switch, message } from "antd";
import { TeamOutlined, PlusOutlined, FireOutlined, PauseOutlined, RadarChartOutlined, ShopOutlined, MessageOutlined } from "@ant-design/icons";
import Scrollbars from "react-custom-scrollbars";
import InfiniteScroll from "react-infinite-scroll-component";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import userIcon from "../../../../assets/images/userId.svg";
import printBill from "../../../../assets/images/printbillIcon.svg";
import DiscountIcon from "../../../../assets/images/discountperIcon.svg";
import Offers from "../../../../assets/images/promoTag.svg";
import { getMultilingualText } from "../../../../utils/getMultilingualText";
import PrintController from "../../../../lib/printer/printController";
import useCartStore from "../../../../stores/useCartStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import { isPrepareBillModificationBlocked, PREPARE_BILL_MODIFICATION_BLOCKED_MESSAGE } from "../../../../utils/prepareBillModificationGuard";
import { recoverOpenPreparedBill, tracePreparedBill } from "../../../../utils/preparedBillRecovery";
import { adoptPreparedBill } from "../../../../utils/cartIdentityGuard";
import { getOpenPreparedBillsForSession, resolveSessionPreparedBill, PREPARED_BILL_LOCKED_MESSAGE } from "../../../../utils/preparedBillTableLock";

const { Text } = Typography;

const CartOrderSidebar = ({
  cart,
  setCart,
  localCart,
  isAllParkedState,
  allItemsFired,
  isReturnOrder,
  saleType,
  groupedProducts,
  hasFiredItems,
  currenciesList,
  pricePrecision,
  toFixedTruncate,
  userPreferences,
  tillData,
  posConfig,
  matchedRep,
  tillaccess,
  salesRepName,
  showDetails,
  setShowDetails,
  serviceChargeAmount,
  tipTotal,
  deliveryChargeAmount,
  packingChargeAmount,
  AdditionalChargesTotal,
  selectedProductId,
  setSelectedProductId,
  setSelectedProductInCart,
  setDisplayCustomerSearch,
  handleAdvanceToggle,
  handleAdditionalCharges,
  handleOrder,
  handlePayBillClick,
  foodServed,
  handleFoodServed,
  processOrder,
  openDisplayParkedBillModal,
  manualDiscountRestaurant,
  setShowSplitBill,
  setCancelKotItems,
  setFireMode,
  setFireItems,
  setSeatGuestDrawerOpen,
  setTransferBill,
  setProdItem,
  setShowProdCenterModal,
  setShowReprintKotDialog,
  openCopyKOTModal,
  setSaleTypeModalItem,
  setSaleTypeModalOpen,
  setSelectedItem,
  setSelectedItemNotes,
  setNotesValue,
  setNotesPopup,
  paymentModalInputRef,
  setPaymentVisible,
  getAddonPreview,
  renderWaiterName,
  decreaseProductQty,
  increaseProductQty,
  loading,
  setLoading,
}) => {
  // TSK-2935: guards Prepare Bill's create-order click against re-entry from a rapid
  // double-click. preparedBillOrigin alone isn't enough — it only flips true after the
  // backend responds, so a second click that lands before the first resolves would still
  // see it unset and treat itself as a failed attempt, resetting layAway/isPrepareBillCreate
  // on the live cart out from under the first click's still-in-flight request.
  const prepareBillInFlightRef = React.useRef(false);

  // Same reasoning for the Place Order / fire-to-kitchen click below: the
  // `loading` prop is driven by the parent's setState and does not update in
  // time to stop a second tap landing in the same tick.
  const fireOrderInFlightRef = React.useRef(false);

  // TSK-3303: once Prepare Bill has confirmed the order, item modifications
  // (fire, hold, production center, sale type, copy KOT, cancel, ...) are
  // blocked unless the till explicitly allows them.
  const isModificationBlocked = isPrepareBillModificationBlocked(cart, posConfig);

  // TIC-11390: the same block when the table has an open Prepare Bill the on-screen cart has
  // lost track of (no preparedBillOrigin) — read from db.orders, see preparedBillTableLock.js.
  // Display only; useVoidHandler.handleVoid enforces it.
  const [tablePreparedBillLocked, setTablePreparedBillLocked] = React.useState(false);
  // Re-checked when the table's KOT lines change (lockLineKey), not on every cart edit.
  const lockLineKey = (cart?.items || []).map((i) => i?.fbOrderLineId || "").join(",");
  const lockItemsRef = React.useRef(cart?.items);
  lockItemsRef.current = cart?.items;
  React.useEffect(() => {
    let alive = true;
    if (!cart?.tableId || cart?.preparedBillOrigin === "Y" || posConfig?.allowModificationAfterPrepareBill === "Y") {
      setTablePreparedBillLocked(false);
      return undefined;
    }
    getOpenPreparedBillsForSession({ tableId: cart.tableId, items: lockItemsRef.current }).then((bills) => {
      if (alive) setTablePreparedBillLocked(bills.length > 0);
    });
    return () => {
      alive = false;
    };
  }, [cart?.tableId, cart?.sOrderID, cart?.preparedBillOrigin, lockLineKey, posConfig?.allowModificationAfterPrepareBill]);

  const getColSpan = () => {
    const count = (posConfig?.enableAdditionalCharges === "Y" ? 1 : 0) + (useRestaurantStore.getState().dineIn === "Y" ? 1 : 0) + (posConfig?.enableSplitBill === "Y" ? 1 : 0);
    return count > 0 ? Math.floor(24 / count) : 24;
  };

  const renderCancelPayBillButtons = () => {
    // TSK-3120: temporary revenue-leakage guard — once Prepare Bill has created a
    // server-confirmed order (preparedBillOrigin:"Y"), the bill has typically already
    // been shown/handed to the customer for payment, so the Cancel button is disabled
    // to block POS-side cancellation. This is interim until an approval-based
    // (OTP/Manager Approval) cancellation flow replaces it.
    // TSK-3303: the "Allow Modification After Prepare Bill" flag now overrides this
    // block when explicitly enabled on the till.
    const cancelBlockedByPrepareBill = isModificationBlocked || tablePreparedBillLocked;

    return (
    <div>
      <Space direction="horizontal" style={{ paddingTop: "2vh", fontSize: "1vw" }}>
        {posConfig?.allowCancelKOT === "Y" && !matchedRep && (
          <Button
            disabled={cancelBlockedByPrepareBill}
            title={cancelBlockedByPrepareBill ? "Bill already prepared — cancellation is disabled" : ""}
            onClick={() => setCancelKotItems(true)}
            style={{
              width: !matchedRep || tillaccess?.waiterPayment === "Y" ? "7.8vw" : "28vw",
              border: "1px solid rgba(146, 144, 152, 0.5)",
              borderRadius: "10px",
              color: "#2F3856",
              height: "6vh",
              fontSize: "1em",
              fontWeight: "500",
              opacity: cancelBlockedByPrepareBill ? 0.5 : 1,
              cursor: cancelBlockedByPrepareBill ? "not-allowed" : "pointer",
            }}
          >
            {getMultilingualText("Cancel")}
          </Button>
        )}
        {(!matchedRep || tillaccess?.waiterPayment === "Y") && (
          <Button
            disabled={loading || (useRestaurantStore.getState().dineIn === "N" ? cart.items.length === 0 : !allItemsFired)}
            onClick={async () => {
              if (loading) return;
              if (useRestaurantStore.getState().dineIn !== "N" && !allItemsFired) return;
              if (isAllParkedState || useRestaurantStore.getState().dineIn === "N") {
                if (saleType === "TA") {
                  const cartObj = useCartStore.getState().cart;
                  const updatedCart = {
                    ...cartObj,
                    sOrderID: cartObj?.preOrder === "Y" ? cartObj.sOrderID : uuidv4().replace(/-/g, "").toUpperCase(),
                  };
                  setCart(updatedCart);
                }
                setPaymentVisible(true);
                setTimeout(() => {
                  paymentModalInputRef.current?.focus();
                  paymentModalInputRef.current?.select();
                }, 200);
              } else {
                // Only this branch writes — handleOrder fires the KOT to the
                // kitchen. `loading` is a prop driven by the parent's setState
                // and does not update in time to stop a second tap in the same
                // tick, so the ref is the real guard here.
                if (fireOrderInFlightRef.current) return;
                try {
                  fireOrderInFlightRef.current = true;
                  setLoading(true);
                  await handleOrder();
                } finally {
                  fireOrderInFlightRef.current = false;
                  setLoading(false);
                }
              }
            }}
            style={{
              width: posConfig?.allowCancelKOT === "N" ? "27.5vw" : "19.5vw",
              borderRadius: "10px",
              backgroundColor: loading ? "#999" : useRestaurantStore.getState().dineIn === "N" ? (cart.items.length > 0 ? "#2F3856" : "#999") : allItemsFired ? "#2F3856" : "#999",
              cursor: loading
                ? "not-allowed"
                : useRestaurantStore.getState().dineIn === "N"
                  ? cart.items.length > 0
                    ? "pointer"
                    : "not-allowed"
                  : allItemsFired
                    ? "pointer"
                    : "not-allowed",
              color: "#fff",
              height: "6vh",
              fontSize: "1em",
              fontWeight: "500",
              opacity: loading ? 0.5 : useRestaurantStore.getState().dineIn === "N" ? (cart.items.length > 0 ? 1 : 0.5) : allItemsFired ? 1 : 0.5,
            }}
          >
            Pay Bill
          </Button>
        )}
      </Space>
    </div>
    );
  };

  const renderAddtionalChargesPrepareSplitBillButtons = () => {
    if ((isAllParkedState || useRestaurantStore.getState().dineIn === "N") && (!matchedRep || tillaccess?.waiterPayment === "Y")) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginTop: "1vh",
            width: "100%",
          }}
        >
          {posConfig?.enableAdditionalCharges === "Y" && (
            <Button
              style={{
                flex: 1,
                height: "5.5vh",
                borderRadius: "10px",
                border: "1px solid rgba(146, 144, 152, 0.5)",
                color: "#2F3856",
                fontSize: "0.9em",
                fontWeight: "500",
              }}
              onClick={handleAdditionalCharges}
            >
              <PlusOutlined />
              Additional Charges
            </Button>
          )}

          {useRestaurantStore.getState().dineIn === "Y" && (
            <Button
              style={{
                flex: 1,
                height: "5.5vh",
                borderRadius: "10px",
                border: "1px solid rgba(146, 144, 152, 0.5)",
                color: "#2F3856",
                fontSize: "0.9em",
                fontWeight: "500",
              }}
              onClick={async () => {
                let liveCart = useCartStore.getState().cart || {};
                const tableName = useRestaurantStore.getState().tableName;

                // BUG-2425: this table session already has an open prepared bill the cart lost
                // track of — reprint that bill instead of creating a second order.
                if (liveCart.preparedBillOrigin !== "Y" && liveCart.tableId && (liveCart.items || []).some((i) => i?.fbOrderLineId)) {
                  const recovered = await recoverOpenPreparedBill(liveCart, "prepareBill");
                  if (recovered) {
                    useCartStore.getState().setCart(recovered);
                    liveCart = recovered;
                  }
                }

                // TIC-11390: an open prepared bill of this table session that BUG-2425's line
                // match could not adopt. Same sOrderID → the cart is that bill: restore it and
                // reprint. Otherwise its lines changed after Prepare Bill (HMRS/10843 → second
                // bill HMRS/10844): blocked unless the till allows modification. When allowed,
                // the new bill is created and the old one is cancelled when the table is paid
                // (processOrder → processOrderApproved) or fully cancelled (handleVoid) — not
                // here, where nothing would send the cancel before the next payment click.
                if (liveCart.preparedBillOrigin !== "Y" && liveCart.tableId) {
                  const sessionBills = await getOpenPreparedBillsForSession({ tableId: liveCart.tableId, items: liveCart.items });
                  const decision = resolveSessionPreparedBill(liveCart, sessionBills);
                  if (decision.action === "adopt") {
                    const adopted = adoptPreparedBill(liveCart, decision.bill);
                    useCartStore.getState().setCart(adopted);
                    liveCart = adopted;
                    tracePreparedBill("prepare-bill-same-id-adopted", { sOrderID: decision.bill.sOrderID, documentno: decision.bill.documentno, tableId: liveCart.tableId });
                  } else if (decision.action === "supersede") {
                    if (posConfig?.allowModificationAfterPrepareBill !== "Y") {
                      message.error(PREPARED_BILL_LOCKED_MESSAGE(decision.bills[0].documentno));
                      tracePreparedBill("prepare-bill-blocked-open-bill", {
                        cartSOrderID: liveCart.sOrderID,
                        tableId: liveCart.tableId,
                        bills: decision.bills.map((b) => ({ sOrderID: b.sOrderID, documentno: b.documentno })),
                      });
                      return;
                    }
                    if (decision.regenerateId) {
                      const freshId = uuidv4().replace(/-/g, "").toUpperCase();
                      useCartStore.getState().setCart((prev) => ({ ...prev, sOrderID: freshId }));
                      liveCart = { ...liveCart, sOrderID: freshId };
                    }
                  }
                }

                // TSK-2935: first Prepare Bill for this order — create a real,
                // server-confirmed order (layAway:"Y", no payment yet) so the document
                // number printed here is the same one the final invoice will carry.
                if (liveCart.preparedBillOrigin !== "Y") {
                  if (prepareBillInFlightRef.current) return;
                  prepareBillInFlightRef.current = true;
                  try {
                    useCartStore.getState().setCart((prev) => ({ ...prev, layAway: "Y", isPrepareBillCreate: "Y" }));
                    await processOrder?.("layaway");
                    const afterCreate = useCartStore.getState().cart || {};
                    if (afterCreate.preparedBillOrigin !== "Y") {
                      // Creation failed (network/validation) — clear the transient flags so a
                      // stale layAway:"Y" doesn't hijack the next payment attempt into the
                      // advance-booking flow (handleOrderCompletion, OrderListComponent.js).
                      useCartStore.getState().setCart((prev) => ({ ...prev, layAway: "N", isPrepareBillCreate: "N" }));
                      return;
                    }
                    handlePayBillClick();
                    const finalCartObj = { ...afterCreate, unsettled: true, tableNo: tableName?.name || afterCreate.tableNo || "" };
                    PrintController(finalCartObj);
                  } finally {
                    prepareBillInFlightRef.current = false;
                  }
                  return;
                }

                // Already created — reprint with the already-confirmed document number,
                // don't create a second order.
                handlePayBillClick();
                const finalCartObj = { ...liveCart, unsettled: true, tableNo: tableName?.name || liveCart.tableNo || "" };
                PrintController(finalCartObj);
              }}
            >
              Prepare Bill
            </Button>
          )}

          {posConfig?.enableSplitBill === "Y" && (
            <Button
              style={{
                flex: 1,
                height: "5.5vh",
                borderRadius: "10px",
                border: "1px solid rgba(146, 144, 152, 0.5)",
                color: "#2F3856",
                fontSize: "0.9em",
                fontWeight: "500",
              }}
              onClick={() => {
                // TSK-2935: Split Bill must happen BEFORE Prepare Bill — once Prepare Bill has
                // confirmed a real server-side order (preparedBillOrigin:"Y"), every split would
                // inherit that same sOrderID while carrying a different suffixed documentno and a
                // fractional total, so each split's confirm call would overwrite the same backend
                // order row instead of creating independent records. Block it here with a clear
                // message instead of letting it silently corrupt the confirmed order.
                if (useCartStore.getState().cart?.preparedBillOrigin === "Y") {
                  message.error("Bill is already prepared — split the bill before preparing it.");
                  return;
                }
                setShowSplitBill(true);
              }}
            >
              <TeamOutlined />
              Split Bill
            </Button>
          )}
        </div>
      );
    }

    return null;
  };

  const renderHoldOrderDiscountButtons = () => {
    if ((isAllParkedState || useRestaurantStore.getState().dineIn === "N") && (!matchedRep || tillaccess?.waiterPayment === "Y")) {
      return (
        <div>
          <Space direction="horizontal" style={{ paddingTop: "2vh", fontSize: "1vw" }}>
            {useRestaurantStore.getState().dineIn !== "Y" && (
              <Button
                style={{
                  width: "13.5vw",
                  borderRadius: "10px",
                  color: "#2F3856",
                  height: "5.5vh",
                  fontSize: "1em",
                  fontWeight: "500",
                }}
                onClick={() => openDisplayParkedBillModal()}
              >
                <img src={printBill} style={{ paddingRight: "0.3rem", height: "2.5vh" }} />
                <span>Hold Order</span>
              </Button>
            )}
            {tillData?.tillAccess?.manualDiscount === "Y" && (
              <Button
                style={{
                  width: "13.5vw",
                  border: "1px solid rgba(146, 144, 152, 0.5)",
                  borderRadius: "10px",
                  color: "#2F3856",
                  height: "5.5vh",
                  fontSize: "1em",
                  fontWeight: "500",
                  marginLeft: "0.3em",
                }}
                onClick={manualDiscountRestaurant}
              >
                <img src={DiscountIcon} style={{ paddingRight: "0.3rem", height: "2.5vh" }} />
                <span>Add Discount</span>
              </Button>
            )}
          </Space>
        </div>
      );
    }
    return null;
  };

  return (
    <div data-zone="orderPanel" data-zone-label="Order" tabIndex={-1} style={{ background: "#fff", height: "100vh", width: "31%", position: "relative", fontSize: "1vw", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "2vh 1vw 0 1vw" }}>
        <span style={{ fontWeight: 600, fontSize: "1.5em", color: "#0F0718" }}>{getMultilingualText("Order Details")}</span>
        <span style={{ fontWeight: 500, fontSize: "1em", color: "#929098" }}>{dayjs(cart.orderTime).format(userPreferences.dateFormat)}</span>
      </div>

      {useRestaurantStore.getState().dineIn === "Y" && cart.tableNo && <p style={{ padding: "0 1vw", fontSize: "1.25em", color: "#0F0718", marginBottom: 0 }}></p>}

      {cart?.tableNo && useRestaurantStore.getState().dineIn === "Y" && (
        <div style={{ display: "flex", justifyContent: "space-between", padding: "0 1vw", marginBottom: "1vh" }}>
          <div
            style={{
              position: "relative",
              background: "#2F3850",
              color: "#fff",
              padding: "4px 26px 4px 10px",
              borderRadius: "6px",
              fontSize: "0.8rem",
              width: "90px",
              cursor: "pointer",
              textAlign: "center",
              userSelect: "none",
            }}
            onClick={() => {
              const tableInfo = useRestaurantStore.getState().tableName;
              if (tableInfo?.isSplitParent === "Y" || tableInfo?.isSplitRow || tableInfo?.splitTables?.length > 0) {
                message.error("Cannot transfer bill for a split table.");
                return;
              }
              setSeatGuestDrawerOpen(true);
              setTransferBill(true);
            }}
          >
            {cart.tableNo}
            <span
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                width: "6px",
                height: "6px",
                borderLeft: "2px solid white",
                borderBottom: "2px solid white",
                transform: "translateY(-50%) rotate(-45deg)",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
      )}

      {/* Customer + Advance row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.2vw 1.2vw", gap: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {tillData.tillAccess.csBunit.b2cCustomer.cwrCustomerId === cart?.customer?.cwrCustomerId ? (
            <button
              style={{
                color: "#0F0718",
                backgroundColor: "#fff",
                border: "1px solid #92909880",
                fontSize: "0.85em",
                borderRadius: "4px",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                padding: "0.2vh 0.5vw",
                cursor: "pointer",
              }}
              onClick={() => setDisplayCustomerSearch(true)}
            >
              <img src={userIcon} style={{ height: 15, width: "1.5vw", marginRight: "0.2vw" }} />
              Add Customer
            </button>
          ) : (
            <button
              style={{
                color: "#0F0718",
                backgroundColor: "#fff",
                border: "1px solid #92909880",
                fontSize: "0.85em",
                borderRadius: "4px",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                padding: "0.2vh 0.5vw",
                cursor: "pointer",
              }}
              onClick={() => setDisplayCustomerSearch(true)}
            >
              <img src={userIcon} style={{ height: 15, width: "1.5vw", marginRight: "0.2vw" }} />
              {cart?.customer?.name}
            </button>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {posConfig?.advanceLayaway === "Y" && useRestaurantStore.getState().dineIn !== "Y" && !isReturnOrder && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontSize: "0.75rem", fontWeight: 500 }}>Advance</span>
              <Switch size="small" checked={cart?.wasLayaway ? !cart.finishLayaway : cart.layAway === "Y"} onChange={(checked) => handleAdvanceToggle(checked)} />
            </div>
          )}
          {renderWaiterName()}
        </div>
      </div>

      <hr style={{ color: "#92909880", width: "93%" }} />

      {/* Dine-in action buttons */}
      {useRestaurantStore.getState().dineIn === "Y" && (
        <>
          <div style={{ padding: "0 1vw", marginTop: "1vh" }}>
            <Button
              disabled={loading || isModificationBlocked}
              loading={loading}
              onClick={async () => {
                if (loading || isModificationBlocked) return;
                try {
                  setLoading(true);
                  setFireMode("all");
                  await handleOrder();
                } finally {
                  setLoading(false);
                }
              }}
              style={{
                backgroundColor: loading || isModificationBlocked ? "#999" : "#4CAF50",
                color: "#fff",
                borderRadius: "8px",
                height: "4.5vh",
                fontSize: "0.9em",
                fontWeight: 600,
                width: "100%",
                marginBottom: "1vh",
              }}
            >
              Fire All to Kitchen
            </Button>
            <div style={{ display: "flex", gap: "1vw", marginBottom: "1.2vh" }}>
              <Button
                onClick={() => setShowReprintKotDialog(true)}
                style={{ flex: 1, height: "4.5vh", borderRadius: "8px", background: "#fff", border: "1px solid #ccc", fontSize: "0.85em", fontWeight: 500 }}
              >
                🧾 Reprint KOT
              </Button>
              <Button
                disabled={isModificationBlocked}
                onClick={() => {
                  if (isModificationBlocked) return;
                  openCopyKOTModal();
                }}
                style={{ flex: 1, height: "4.5vh", borderRadius: "8px", background: "#fff", border: "1px solid #ccc", fontSize: "0.85em", fontWeight: 500, opacity: isModificationBlocked ? 0.5 : 1 }}
              >
                📋 Copy KOT
              </Button>
              <Button
                disabled={!hasFiredItems || foodServed}
                onClick={() => {
                  if (!hasFiredItems || foodServed) return;
                  handleFoodServed?.();
                }}
                style={{
                  flex: 1,
                  height: "4.5vh",
                  borderRadius: "8px",
                  fontSize: "0.85em",
                  fontWeight: 500,
                  background: foodServed ? "#4CAF50" : "#fff",
                  color: foodServed ? "#fff" : "#0F0718",
                  border: foodServed ? "1px solid #4CAF50" : "1px solid #ccc",
                  opacity: !hasFiredItems ? 0.5 : 1,
                }}
              >
                {foodServed ? "✓ Food Served" : "🍽️ Food Served"}
              </Button>
            </div>
          </div>
          <hr style={{ color: "#92909880", width: "93%" }} />
        </>
      )}

      {/* Cart items scrollable list */}
      <div style={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
      <Scrollbars autoHide={false} style={{ height: "100%", width: "100%" }}>
        <InfiniteScroll
          dataLength={cart.items.length}
          hasMore={true}
          style={{ height: "100%" }}
          endMessage={
            <p className="center">
              <b>Yay! you have seen it all</b>
            </p>
          }
        >
          <div>
            {Object.keys(groupedProducts).map(
              (category, index) =>
                groupedProducts[category].some((item) => item.order !== "Y") && (
                  <div key={index}>
                    <Text style={{ fontSize: "1em", fontWeight: "500", paddingLeft: "1rem", color: "#0F0718" }}>
                      {category} ({groupedProducts[category].filter((item) => item.order !== "Y").length})
                    </Text>
                    {groupedProducts[category]
                      .filter((item) => item.order !== "Y")
                      .map((item) => (
                        <div key={item.lineId} style={{ padding: "1px 10px" }}>
                          <div
                            style={{
                              background: "#fff",
                              borderRadius: "12px",
                              border: selectedProductId === item.lineId ? "1px solid #b8c2cc" : "1px solid #E5E7EB",
                              padding: "12px 14px",
                              marginBottom: "10px",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              if (item.parkedItem) return;
                              setSelectedProductId(item.lineId);
                              setSelectedProductInCart(item);
                            }}
                          >
                            {/* TOP ROW */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    fontSize: "0.8rem",
                                    maxWidth: "160px",
                                    display: "inline-block",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    verticalAlign: "bottom",
                                  }}
                                  title={item?.productAliasName ? item?.productAliasName : item.name}
                                >
                                  {item?.productAliasName ? item?.productAliasName : item.name}
                                </span>
                                <div
                                  style={{
                                    background: item.isHold ? "#FDE68A" : "#F3F4F6",
                                    padding: "1px 6px",
                                    fontSize: "0.6rem",
                                    borderRadius: 4,
                                  }}
                                >
                                  {item.isHold ? "Hold" : "Editing"}
                                </div>
                              </div>
                              <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>
                                {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                                {toFixedTruncate(item.nettotal, pricePrecision)}
                                {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol}` : ""}
                              </span>
                            </div>

                            {/* ADDONS + NOTES */}
                            <div style={{ marginTop: 4, fontSize: "0.75rem", color: "#555", display: "flex", alignItems: "center", gap: "8px", maxWidth: "100%" }}>
                              {item?.selectedAddons?.length > 0 && (
                                <span
                                  style={{ fontSize: "0.65rem", color: "#6B7280", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "180px" }}
                                  title={item.selectedAddons.map((a) => a.name).join(", ")}
                                >
                                  {item.selectedAddons.map((a) => a.name).join(", ")}
                                </span>
                              )}
                              {item.notes && (
                                <span
                                  style={{ fontSize: "0.65rem", color: "#6B7280", maxWidth: "200px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                  title={item.notes}
                                >
                                  📝 {item.notes}
                                </span>
                              )}
                            </div>

                            {/* QTY + ACTION ICONS */}
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10 }}>
                              <div
                                style={{ display: "flex", alignItems: "center", gap: "10px", background: "#F3F4F6", padding: "6px 10px", borderRadius: "8px", fontSize: "0.8rem" }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "1rem" }} onClick={() => decreaseProductQty(item)}>
                                  –
                                </button>
                                <span>{item.weight || item.qty}</span>
                                <button style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: "1rem" }} onClick={() => increaseProductQty(item)}>
                                  +
                                </button>
                              </div>

                              {/* ICONS */}
                              <div style={{ display: "flex", alignItems: "center", gap: "14px", fontSize: 18 }}>
                                {useRestaurantStore.getState().dineIn === "Y" && (
                                  <FireOutlined
                                    style={{ cursor: isModificationBlocked ? "not-allowed" : "pointer", opacity: isModificationBlocked ? 0.5 : 1 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isModificationBlocked) {
                                        message.info(PREPARE_BILL_MODIFICATION_BLOCKED_MESSAGE);
                                        return;
                                      }
                                      setFireMode("single");
                                      setFireItems([item]);
                                      handleOrder([item]);
                                    }}
                                  />
                                )}
                                {useRestaurantStore.getState().dineIn === "Y" && (
                                  <PauseOutlined
                                    style={{ cursor: isModificationBlocked ? "not-allowed" : "pointer", opacity: isModificationBlocked ? 0.5 : 1 }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isModificationBlocked) {
                                        message.info(PREPARE_BILL_MODIFICATION_BLOCKED_MESSAGE);
                                        return;
                                      }
                                      const updatedItems = cart.items.map((i) => {
                                        if (i.lineId === item.lineId) {
                                          if (i.isHold) {
                                            return { ...i, isHold: false, order: "N", firedQty: 0 };
                                          } else {
                                            return { ...i, isHold: true };
                                          }
                                        }
                                        return i;
                                      });
                                      setCart({ ...cart, items: updatedItems });
                                    }}
                                  />
                                )}
                                {useRestaurantStore.getState().dineIn === "Y" && (
                                  <RadarChartOutlined
                                    style={{ cursor: isModificationBlocked ? "not-allowed" : "pointer", opacity: isModificationBlocked ? 0.5 : 1, color: item.productionCenterId ? "#715C63" : "#2F3856" }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isModificationBlocked) {
                                        message.info(PREPARE_BILL_MODIFICATION_BLOCKED_MESSAGE);
                                        return;
                                      }
                                      setProdItem(item);
                                      setShowProdCenterModal(true);
                                    }}
                                  />
                                )}
                                {useRestaurantStore.getState().dineIn === "Y" && (
                                  <ShopOutlined
                                    style={{
                                      cursor: isModificationBlocked ? "not-allowed" : "pointer",
                                      opacity: isModificationBlocked ? 0.5 : 1,
                                      color: item.saleType ? (item.saleType === "DI" ? "#715C63" : "#00B894") : "#2F3856",
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isModificationBlocked) {
                                        message.info(PREPARE_BILL_MODIFICATION_BLOCKED_MESSAGE);
                                        return;
                                      }
                                      setSaleTypeModalItem(item);
                                      setSaleTypeModalOpen(true);
                                    }}
                                  />
                                )}
                                <MessageOutlined
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedItem(item);
                                    setSelectedItemNotes(item);
                                    setNotesValue(item.notes || "");
                                    setNotesPopup(true);
                                  }}
                                />
                              </div>
                            </div>

                            {/* DISCOUNT BADGE */}
                            {Math.abs(item.discount) > 0 && (
                              <span
                                style={{
                                  background: "#F8C519",
                                  borderRadius: 3,
                                  padding: "2px 5px",
                                  fontSize: "0.65rem",
                                  marginTop: 6,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <img src={Offers} alt="" width={12} height={12} />
                                {item.discountName}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    <hr style={{ color: "#92909880", width: "92.5%" }} />
                  </div>
                )
            )}

            {/* Fired items inline in main scroll */}
            {hasFiredItems && (
              <>
                <div style={{ margin: "0px 0 0px 10px", fontWeight: 500, fontSize: "0.7rem", color: "#6B7280" }}>Fired Items</div>
                {Object.keys(groupedProducts).map(
                  (category, index) =>
                    groupedProducts[category].some((item) => item.order === "Y") && (
                      <div key={`fired-${index}`}>
                        <Text style={{ fontSize: "0.8em", fontWeight: 500, paddingLeft: "0.6rem", color: "#9CA3AF" }}>
                          {category} ({groupedProducts[category].filter((item) => item.order === "Y").length})
                        </Text>
                        {groupedProducts[category]
                          .filter((item) => item.order === "Y")
                          .map((item) => (
                            <div
                              key={item.lineId}
                              onClick={() => {
                                if (item.parkedItem) return;
                                if (useRestaurantStore.getState().dineIn === "N") {
                                  setSelectedProductId(item.lineId);
                                  setSelectedProductInCart(item);
                                }
                              }}
                            >
                              <div style={{ width: "100%", padding: "2px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "0.9rem" }}>
                                <div
                                  title={item.selectedAddons?.length > 2 ? item.selectedAddons.map((a) => a.name).join(", ") : ""}
                                  style={{
                                    fontWeight: 400,
                                    fontSize: "0.7rem",
                                    color: item.isCanceled === "Y" ? "#9CA3AF" : selectedProductId === item.lineId ? "#0F0718" : "#afb7c3",
                                    marginLeft: 7,
                                    cursor: item.isCanceled === "Y" ? "not-allowed" : "pointer",
                                    backgroundColor: "transparent",
                                    textDecoration: item.isCanceled === "Y" ? "line-through" : "none",
                                  }}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    if (item.isCanceled === "Y") return;
                                    setSelectedProductId(item.lineId);
                                    setSelectedProductInCart(item);
                                  }}
                                >
                                  {item?.productAliasName ? item?.productAliasName : item.name}
                                  {item.selectedAddons?.length > 0 && (
                                    <span style={{ color: "#afb7c3" }}>
                                      {" ("}
                                      {getAddonPreview(item.selectedAddons)}
                                      {")"}
                                    </span>
                                  )}
                                  <span style={{ color: "#afb7c3" }}> × {item.weight}</span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 6, marginRight: 5 }}>
                                  <div
                                    style={{
                                      fontSize: "0.7rem",
                                      fontWeight: 400,
                                      color: item.isCanceled === "Y" ? "#9CA3AF" : "#afb7c3",
                                      textDecoration: item.isCanceled === "Y" ? "line-through" : "none",
                                    }}
                                  >
                                    {currenciesList[0].symbolRightSide === "Y" && <span>{currenciesList[0].currSymbol}&nbsp;</span>}
                                    {toFixedTruncate(item.nettotal, pricePrecision)}
                                    {currenciesList[0].symbolRightSide === "N" && <span>&nbsp;{currenciesList[0].currSymbol}</span>}
                                  </div>
                                  <div
                                    style={{
                                      background: item.isCanceled === "Y" ? "#db4444ff" : "#9fa4ae",
                                      color: "#fff",
                                      padding: "0px 7px",
                                      borderRadius: 4,
                                      fontSize: "0.6rem",
                                      fontWeight: 400,
                                      marginRight: 5,
                                    }}
                                  >
                                    {item.isCanceled === "Y" ? "Cancelled" : "Fired"}
                                  </div>
                                </div>
                              </div>
                              {Math.abs(item.discount) > 0 && item.discountName && (
                                <span
                                  style={{
                                    background: "#F8C519",
                                    borderRadius: "3px",
                                    padding: "0.1vw",
                                    fontSize: "0.65em",
                                    marginLeft: "0.6vw",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <img src={Offers} alt="" width={12} height={12} />
                                  {item.discountName}
                                </span>
                              )}
                            </div>
                          ))}
                        <hr style={{ color: "#92909880", width: "92.5%" }} />
                      </div>
                    )
                )}
              </>
            )}
          </div>
        </InfiniteScroll>
      </Scrollbars>
      </div>

      {/* Bottom totals + actions */}
      <div data-zone="orderActions" data-zone-label="Actions" tabIndex={-1} style={{ padding: "1vh 0.6vw", fontSize: "1vw", width: "100%" }}>
        <div
          onClick={() => setShowDetails(!showDetails)}
          style={{
            cursor: "pointer",
            fontWeight: 500,
            fontSize: "1em",
            background: "#F3F4F9",
            padding: "0.5vh 0.5vw",
            borderRadius: "5px",
            marginBottom: "1vh",
          }}
        >
          View Details <span style={{ color: "#6B7280" }}>{showDetails ? "▲" : "▼"}</span>
        </div>
        {showDetails && (
          <Scrollbars style={{ height: "18vh" }}>
            <Space direction="vertical" style={{ width: "100%", background: "#F3F4F9", padding: "1vh 0.5vw", borderRadius: "10px", rowGap: "4px" }}>
              <Space style={{ width: "100%", justifyContent: "space-between" }}>
                <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>{getMultilingualText("Subtotal")}</Text>
                <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>
                  {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                  {toFixedTruncate(cart?.total - cart?.tax, pricePrecision)}
                  {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol} ` : ""}
                </Text>
              </Space>
              <Space style={{ width: "100%", justifyContent: "space-between" }}>
                <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>{getMultilingualText("Discount Sales")}</Text>
                <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>
                  {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                  {toFixedTruncate(cart.discount, pricePrecision)}
                  {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol} ` : ""}
                </Text>
              </Space>
              <Space style={{ width: "100%", justifyContent: "space-between" }}>
                <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>{getMultilingualText("Total Sales Tax")}</Text>
                <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>
                  {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                  {toFixedTruncate(cart.tax, pricePrecision)}
                  {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol} ` : ""}
                </Text>
              </Space>
              {serviceChargeAmount > 0 && (
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>{getMultilingualText("Service Charge")}</Text>
                  <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>
                    {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                    {toFixedTruncate(serviceChargeAmount, pricePrecision)}
                    {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol} ` : ""}
                  </Text>
                </Space>
              )}
              {tipTotal > 0 && (
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>{getMultilingualText("Tip")}</Text>
                  <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>
                    {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                    {toFixedTruncate(tipTotal, pricePrecision)}
                    {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol} ` : ""}
                  </Text>
                </Space>
              )}
              {deliveryChargeAmount > 0 && (
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>{getMultilingualText("Delivery Charge")}</Text>
                  <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>
                    {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                    {toFixedTruncate(deliveryChargeAmount, pricePrecision)}
                    {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol} ` : ""}
                  </Text>
                </Space>
              )}
              {packingChargeAmount > 0 && (
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>{getMultilingualText("Packing Charge")}</Text>
                  <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>
                    {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                    {toFixedTruncate(packingChargeAmount, pricePrecision)}
                    {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol} ` : ""}
                  </Text>
                </Space>
              )}
              {cart.roundOff !== 0 && cart.roundOff !== undefined && (
                <Space style={{ width: "100%", justifyContent: "space-between" }}>
                  <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>{getMultilingualText("Round Off")}</Text>
                  <Text style={{ fontSize: "1em", fontWeight: 500, color: "#929098", fontFamily: "Inter" }}>
                    {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
                    {toFixedTruncate(cart.roundOff, pricePrecision)}
                    {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol} ` : ""}
                  </Text>
                </Space>
              )}
            </Space>
          </Scrollbars>
        )}
        <Space style={{ width: "100%", justifyContent: "space-between", marginTop: "2px" }}>
          <Text style={{ fontSize: "1.1em", color: "#0F0718", fontWeight: 600 }}>{getMultilingualText("Total Amount")}</Text>
          <Text style={{ fontSize: "1.1em", color: "#0F0718", fontWeight: 600 }}>
            {currenciesList[0].symbolRightSide === "Y" ? `${currenciesList[0].currSymbol} ` : ""}
            {toFixedTruncate(parseFloat(cart.total) + AdditionalChargesTotal, pricePrecision)}
            {currenciesList[0].symbolRightSide === "N" ? ` ${currenciesList[0].currSymbol} ` : ""}
          </Text>
        </Space>
        {renderAddtionalChargesPrepareSplitBillButtons()}
        {renderHoldOrderDiscountButtons()}
        {renderCancelPayBillButtons()}
      </div>
    </div>
  );
};

export default React.memo(CartOrderSidebar);
